import {useCallback} from "react";
import {Class} from "@/lib/legacyApi/fetchClassesForCourseByCampusId";
import {Dialog, DialogTrigger, Modal} from "react-aria-components";
import ModalBody from "@/app/_components/ModalBody";
import ModalOverlay from "@/app/_components/ModalOverlay";
import EnrollModalBody from "./EnrollModalBody";
import RACButton from "@/app/_components/RACButton";
import {useUserInfo} from "@/app/_components/contexts/userInfoContext";
import {STUDENT_ROLES, hasAnyRole} from "@/app/util/userUtils";
import clsMerge from "../../../../../util/clsMerge";
import useEnrollModalInfo from "../useEnrollModalInfo";
import {FCWithChildren} from "@/app/types/reactTypes";
import {ButtonVariantProps} from "@/app/_components/Button";
import {AbbreviatedLegacyClass} from "@/shared/legacyTypes/fetchAllClassesForCampusTypes";

interface EnrollInClassButtonProps extends ButtonVariantProps {
	gtmId: string;
	class: Class | AbbreviatedLegacyClass;
}

const EnrollInClassButton: FCWithChildren<EnrollInClassButtonProps> = ({
	class: classToAdd,
	children,
	gtmId,
	...buttonVariantProps
}) => {
	const {userInfo} = useUserInfo();
	const {
		students,
		isConsultationRequired,
		isLoggedIn,
		isLoading,
		isOpen,
		isSummer,
		setIsOpen,
		triggerLoad,
	} = useEnrollModalInfo(classToAdd);
	const onPress = useCallback(() => {
		if (isLoading) {
			return;
		}
		triggerLoad();
	}, [isLoading, triggerLoad]);
	return (
		<DialogTrigger>
			<RACButton
				id={gtmId}
				className={clsMerge(
					"enroll-button rounded-md px-4",
					isLoading && "opacity-50"
				)}
				onPress={onPress}
				isDisabled={hasAnyRole(userInfo, STUDENT_ROLES)}
				data-course-id={classToAdd.courseId}
				data-class-id={classToAdd.classId}
				data-campus-id={classToAdd.campusId}
				{...buttonVariantProps}
			>
				{isLoading ? "..." : children}
			</RACButton>
			<ModalOverlay
				isDismissable
				isOpen={isOpen && !isLoading}
				onOpenChange={setIsOpen}
			>
				<Modal>
					<Dialog>
						<ModalBody
							onClose={() => setIsOpen(false)}
							className="h-full w-full rounded-xl px-0 py-0 md:px-0"
						>
							<EnrollModalBody
								class={classToAdd}
								close={() => setIsOpen(false)}
								students={students}
								isLoggedIn={isLoggedIn}
								isConsultationRequired={isConsultationRequired}
								isSummer={isSummer}
							/>
						</ModalBody>
					</Dialog>
				</Modal>
			</ModalOverlay>
		</DialogTrigger>
	);
};

export default EnrollInClassButton;
